import React, {useState, useCallback, useEffect} from "react";
import cn from "classnames";
import PropTypes from "prop-types";
import Dialog from "components/dialog";
import IconButton from "components/icon-button";
import Icon from "components/icon";
import withStyles from "components/with-styles";
import ActionButton from "./action-button";
import DisabledTooltip from "components/tooltip";
import styles from "./styles/action-with-modal.css.js";
import { useLoadingContext } from "components/loading-spinner-overlay";
import {useIsomorphicEffect} from "@material-ui/pickers/_shared/hooks/useKeyDown";

const UnstyledActionWithModalControlled = ({
  actionButton = false,
  mutation,
  mutationVariables,
  elementNameInResult,
  dialogTitle,
  renderDialogContent,
  classes,
  onSuccess = () => {},
  onError = () => {},
  icon,
  disabled = false,
  disabledTooltipText,
  showLoading = false,
  showModalAccept = true,
  showModalClose = true,
  onClose,
  onOpen,
  event,
  refetchData,
  disableIcon,
  item,
  onAccept,
  className,
  intl,
  open,
  setOpen,
}) => {
  const { setLoading } = useLoadingContext();

  const handleOpen = useCallback(
    e => {
      setOpen(true);
      e.stopPropagation();
      onOpen && onOpen();
    },
    [onOpen, setOpen],
  );

  const handleClose = useCallback(
    e => {
      setOpen(false);
      e.stopPropagation();
      onClose && onClose();
    },
    [onClose, setOpen],
  );

  const handleAccept = useCallback(
    e => {
      if (showLoading) {
        setLoading(true);
      }
      if (mutation) {
        mutation({
          variables: mutationVariables,
        })
          .then(result => {
            if (result && result.data && result.data[elementNameInResult]) {
              if (onSuccess) {
                onSuccess();
              }
            } else {
              if (onError) {
                onError();
              }
            }
          })
          .catch(onError)
          .finally(() => {
            if (showLoading) {
              setLoading(false);
            }
          });
      } else if (onAccept) {
        onAccept();
      }

      setOpen(false);
      e.stopPropagation();
    },
    [elementNameInResult, mutation, mutationVariables, onAccept, onError, onSuccess, setLoading, setOpen, showLoading],
  );

  return (
    <>
      {actionButton && !!icon ? (
        <ActionButton
          className={cn(classes.actionButton, className)}
          disabledTooltipText={disabledTooltipText}
          disabled={disabled}
          icon={icon}
          onClick={handleOpen}
        />
      ) : (
        <DisabledTooltip disabled={disabled} text={disabledTooltipText}>
          <div className={cn(classes.tooltipContainer, className)}>
           { !disableIcon && <IconButton disabled={disabled} className={cn(classes.iconButton, className)} onClick={handleOpen}>
             {!!icon && <Icon name={icon}/>}
            </IconButton>}
          </div>
        </DisabledTooltip>
      )}
      <Dialog
        intl={intl}
        className={classes.dialog}
        title={dialogTitle}
        open={open}
        event={event}
        onSuccess={refetchData}
        item={item}
        showModalClose={showModalClose}
        showModalAccept={showModalAccept}
        onClose={handleClose}
        onAccept={handleAccept}
      >
        {renderDialogContent({ handleAccept, handleClose })}
      </Dialog>
    </>
  );
};

UnstyledActionWithModalControlled.propTypes = {
  /**
   * Texto para mostrar en un tooltip en caso de que el botón esté deshabilitado.
   * En caso de no haber texto, no se muestra el tooltip
   **/
  disabledTooltipText: PropTypes.string,
  /**
   * Indica si se muestra el overlay de carga
   **/
  showLoading: PropTypes.bool,
  /**
   * Indica si el botón está deshabilitado
   **/
  disabled: PropTypes.bool,
  /**
   * Callback si hay éxito
   **/
  onSuccess: PropTypes.func,
  /**
   * Callback si hay un error
   **/
  onError: PropTypes.func,
  /**
   * Muestra el botón de aceptar
   **/
  showModalAccept: PropTypes.bool,
  /**
   * Muestra el botón de cancelar/cerrar
   **/
  showModalClose: PropTypes.bool,
  /**
   * Nombre del icono a mostrar
   **/
  icon: PropTypes.string,
  /**
   * Indica si se muestra como un botón de acción
   **/
  actionButton: PropTypes.bool,
  /**
   * Función mutación de Apollo
   **/
  mutation: PropTypes.func,
  /**
   * Objeto variables de parámetros de mutación
   **/
  mutationVariables: PropTypes.object,
  /**
   * Nombre del elemento en el resultado de la query (result.data[elementNameInResult].id)
   **/
  elementNameInResult: PropTypes.string,
  /**
   * Título del diálogo
   **/
  dialogTitle: PropTypes.string,
  /**
   * Función para renderizar el contenido del diálogo
   **/
  renderDialogContent: PropTypes.func,
  /**
   * Función que se invoca al cerrar el diálogo
   **/
  onClose: PropTypes.func,
  /**
   * Función que se invoca al abrir el diálogo
   **/
  onOpen: PropTypes.func,
  /**
   * Función que se invoca al aceptar el diálogo
   **/
  onAccept: PropTypes.func,
  /**
   * Clase adicional del componente
   **/
  className: PropTypes.string,
  /**
   * Estado que indica si está abierta o no la modal
   **/
  open: PropTypes.bool.isRequired,
  /**
   * Función que actualiza el estado "open"
   **/
  setOpen: PropTypes.func.isRequired,
};

export const ActionWithModalControlled = withStyles(styles)(UnstyledActionWithModalControlled);

const ActionWithModal = props => {
  const [open, setOpen] = useState(false);
  return <ActionWithModalControlled open={open} setOpen={setOpen} {...props} />;
};

export default ActionWithModal;
